import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Building2, MessageSquare, Gauge, User, Tag as TagIcon, Plus, Trash2, Database, AlertTriangle } from "lucide-react";

export default function Settings() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState("");
  const [clearTarget, setClearTarget] = useState(null);
  const [clearPassword, setClearPassword] = useState("");

  const doClear = async () => {
    if (!clearTarget) return;
    if (!clearPassword) return toast.error("Digite a senha do administrador.");
    try {
      const { data } = await api.post("/maintenance/clear", { target: clearTarget.key, password: clearPassword });
      toast.success(`${data.deleted} registro(s) apagado(s).`);
      setClearTarget(null);
      setClearPassword("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const loadTags = () => api.get("/tags").then((r) => setTags(r.data));
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); loadTags(); }, []);

  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const save = async () => {
    try {
      await api.put("/settings", {
        clinic_name: s.clinic_name, clinic_phone: s.clinic_phone, clinic_email: s.clinic_email,
        interval_min: parseInt(s.interval_min) || 0, interval_max: parseInt(s.interval_max) || 0,
        max_messages: s.max_messages ? parseInt(s.max_messages) : null,
        allowed_start: s.allowed_start, allowed_end: s.allowed_end,
      });
      toast.success("Configurações salvas!");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const addTag = async () => {
    if (!newTag.trim()) return;
    try { await api.post("/tags", { name: newTag.trim().toUpperCase() }); setNewTag(""); loadTags(); toast.success("Tag criada!"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const delTag = async (id) => { await api.delete(`/tags/${id}`); loadTags(); toast.success("Tag removida."); };

  if (!s) return null;

  return (
    <div className="space-y-6 animate-fade-up max-w-3xl" data-testid="settings-page">
      <div><h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Configurações</h1><p className="text-sm text-slate-500 mt-1">Ajustes da clínica, disparos, tags e administrador.</p></div>

      <Tabs defaultValue="clinic">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="clinic" data-testid="settings-tab-clinic"><Building2 className="h-4 w-4 mr-1" /> Clínica</TabsTrigger>
          <TabsTrigger value="dispatch" data-testid="settings-tab-dispatch"><Gauge className="h-4 w-4 mr-1" /> Disparos</TabsTrigger>
          <TabsTrigger value="tags" data-testid="settings-tab-tags"><TagIcon className="h-4 w-4 mr-1" /> Tags</TabsTrigger>
          <TabsTrigger value="data" data-testid="settings-tab-data"><Database className="h-4 w-4 mr-1" /> Dados</TabsTrigger>
          <TabsTrigger value="admin" data-testid="settings-tab-admin"><User className="h-4 w-4 mr-1" /> Admin</TabsTrigger>
        </TabsList>

        <TabsContent value="clinic">
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm space-y-4">
            <div><Label>Nome da clínica</Label><Input value={s.clinic_name || ""} onChange={(e) => set("clinic_name", e.target.value)} className="mt-1.5" data-testid="settings-clinic-name" /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label>Telefone</Label><Input value={s.clinic_phone || ""} onChange={(e) => set("clinic_phone", e.target.value)} className="mt-1.5" /></div>
              <div><Label>E-mail</Label><Input value={s.clinic_email || ""} onChange={(e) => set("clinic_email", e.target.value)} className="mt-1.5" /></div>
            </div>
            <Button onClick={save} className="bg-emerald-500 hover:bg-emerald-600" data-testid="settings-save-clinic">Salvar</Button>
          </div>
        </TabsContent>

        <TabsContent value="dispatch">
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Intervalo mínimo (s)</Label><Input type="number" value={s.interval_min ?? 1} onChange={(e) => set("interval_min", e.target.value)} className="mt-1.5" data-testid="settings-interval-min" /></div>
              <div><Label>Intervalo máximo (s)</Label><Input type="number" value={s.interval_max ?? 3} onChange={(e) => set("interval_max", e.target.value)} className="mt-1.5" data-testid="settings-interval-max" /></div>
            </div>
            <div><Label>Máximo de mensagens por campanha</Label><Input type="number" value={s.max_messages || ""} onChange={(e) => set("max_messages", e.target.value)} placeholder="Sem limite" className="mt-1.5" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Horário permitido — início</Label><Input type="time" value={s.allowed_start || "08:00"} onChange={(e) => set("allowed_start", e.target.value)} className="mt-1.5" /></div>
              <div><Label>Horário permitido — fim</Label><Input type="time" value={s.allowed_end || "20:00"} onChange={(e) => set("allowed_end", e.target.value)} className="mt-1.5" /></div>
            </div>
            <p className="text-xs text-slate-400">O intervalo entre mensagens evita disparos rápidos demais e reduz risco de bloqueio.</p>
            <Button onClick={save} className="bg-emerald-500 hover:bg-emerald-600" data-testid="settings-save-dispatch">Salvar</Button>
          </div>
        </TabsContent>

        <TabsContent value="tags">
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm space-y-4">
            <div className="flex gap-2">
              <Input value={newTag} onChange={(e) => setNewTag(e.target.value)} placeholder="Nova tag (ex: VIP)" onKeyDown={(e) => e.key === "Enter" && addTag()} data-testid="settings-new-tag" />
              <Button onClick={addTag} className="bg-emerald-500 hover:bg-emerald-600" data-testid="settings-add-tag"><Plus className="h-4 w-4" /></Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {tags.map((t) => (
                <span key={t.id} className="inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">
                  {t.name} <span className="text-xs opacity-60">{t.count}</span>
                  <button onClick={() => delTag(t.id)} className="text-emerald-400 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                </span>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="data">
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-soft space-y-4" data-testid="settings-data-tab">
            <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-100">
              <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">Estas ações apagam dados <strong>permanentemente</strong> e não podem ser desfeitas. Os cadastros dos clientes não são afetados.</p>
            </div>
            <DangerRow
              title="Apagar histórico de mensagens"
              desc="Remove todas as mensagens da página Histórico e do histórico de comunicação nos perfis dos clientes."
              onClick={() => setClearTarget({ key: "messages", label: "histórico de mensagens" })}
              testid="clear-messages"
            />
            <DangerRow
              title="Apagar histórico de disparos (campanhas)"
              desc="Remove todas as campanhas e seus contatos. Não afeta os clientes nem o histórico de mensagens."
              onClick={() => setClearTarget({ key: "campaigns", label: "histórico de disparos" })}
              testid="clear-campaigns"
            />
            <DangerRow
              title="Apagar linha do tempo dos clientes"
              desc="Remove todos os eventos da timeline (cadastro, mensagens, tags) nos perfis dos clientes."
              onClick={() => setClearTarget({ key: "timeline", label: "linha do tempo dos clientes" })}
              testid="clear-timeline"
            />
          </div>
        </TabsContent>

        <TabsContent value="admin">
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm space-y-3">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-emerald-500 text-white flex items-center justify-center font-semibold">{user?.name?.[0]}</div>
              <div><p className="font-semibold text-slate-800">{user?.name}</p><p className="text-sm text-slate-500">{user?.email}</p></div>
            </div>
            <p className="text-xs text-slate-400 pt-2 border-t border-slate-100">Este é o único acesso ao sistema. Para alterar a senha, use "Recuperar senha" na tela de login.</p>
          </div>
        </TabsContent>
      </Tabs>

      <AlertDialog open={!!clearTarget} onOpenChange={(o) => { if (!o) { setClearTarget(null); setClearPassword(""); } }}>
        <AlertDialogContent data-testid="clear-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Apagar {clearTarget?.label}</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação é permanente e não pode ser desfeita. Por segurança, confirme com a senha do administrador ({user?.email}) para apagar o {clearTarget?.label}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-1">
            <Label htmlFor="clear-pass">Senha do administrador</Label>
            <Input
              id="clear-pass" type="password" value={clearPassword}
              onChange={(e) => setClearPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doClear()}
              placeholder="••••••••" className="mt-1.5" autoFocus data-testid="clear-password-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <Button onClick={doClear} disabled={!clearPassword} className="bg-red-500 hover:bg-red-600" data-testid="confirm-clear">Apagar</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function DangerRow({ title, desc, onClick, testid }) {
  return (
    <div className="flex items-center gap-4 p-4 rounded-xl border border-red-100 bg-red-50/40">
      <div className="flex-1">
        <p className="font-medium text-slate-800">{title}</p>
        <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
      </div>
      <Button variant="outline" onClick={onClick} className="border-red-200 text-red-600 hover:bg-red-50 shrink-0" data-testid={testid}>
        <Trash2 className="h-4 w-4 mr-2" /> Apagar
      </Button>
    </div>
  );
}
